import type { PlaygroundModeStatus } from '@/http/Company/playgroundModeApi';
import queryKeys from '@/util/hooks/queries/queryKeys';

import { queryClient } from '../../root';

/**
 * Request header every outbound B1Link API call carries when Playground Mode is active for the
 * current session (Bugs #8515/#8519). Every service except B1LinkCompany.Controllers.API has no
 * way to read Company's in-process IPlaygroundModeStore, so the frontend — which already holds
 * the server-confirmed toggle state via usePlaygroundMode's query cache — carries it with the
 * request instead. See B1Link.Playground's HeaderPlaygroundContext for the backend half.
 */
export const PLAYGROUND_MODE_HEADER_NAME = 'X-Playground-Mode';
const PLAYGROUND_MODE_HEADER_ACTIVE_VALUE = 'active';

/**
 * The Playground Mode routes (GET status, POST on/off, and anything added under them) never carry the header (Bug #8689). Company reads
 * its own IPlaygroundModeStore, so the header is meaningless there — and if any ingress's CORS
 * preflight rejects it, attaching it to the OFF call is what leaves a tenant stuck in Playground
 * Mode. The switch must not depend on the thing it switches off.
 */
const PLAYGROUND_MODE_TOGGLE_PATH = /\/api\/Company\/playground-mode(\/|\?|$)/i;

/**
 * Reads the same query cache entry usePlaygroundMode.ts populates, without using the hook.
 *
 * Known gap (auto-review finding, fp:playground-header-empty-cache-fails-open, PR #2609): this
 * cache entry is `undefined` until usePlaygroundMode's own GET first resolves, so any API call
 * that races that initial fetch — on a fresh load or hard refresh — carries no header and the
 * backend sends for real, even if the session's Playground state (from a previous page load, or
 * another tab) is actually ON. Fails in the unsafe direction, but only during that narrow
 * first-load window; the two flows #8515/#8519 target are both user-initiated well after the app
 * has settled. Not fixed here: awaiting the in-flight query from every onRequest hook would add
 * latency to every request in the app for a window this narrow.
 */
export const getPlaygroundModeHeaders = (requestUrl?: string): Record<string, string> => {
  if (requestUrl && PLAYGROUND_MODE_TOGGLE_PATH.test(requestUrl)) {
    return {};
  }
  const status = queryClient.getQueryData<PlaygroundModeStatus>([
    queryKeys.playgroundMode
  ]);
  return status?.isActive
    ? { [PLAYGROUND_MODE_HEADER_NAME]: PLAYGROUND_MODE_HEADER_ACTIVE_VALUE }
    : {};
};

/** For the openapi-fetch clients' `onRequest` hooks — mirrors how they already call rememberRequestBody. */
export const applyPlaygroundModeHeader = (request: Request): void => {
  Object.entries(getPlaygroundModeHeaders(request.url)).forEach(([name, value]) => {
    request.headers.set(name, value);
  });
};
